import { z } from 'zod'
import { editBaseRevisionSchema } from '../edit-revision'
import { PRICE_EVIDENCE_SOURCES, PRICE_EVIDENCE_STATUSES } from '../cost-evidence'
import { PRODUCT_CATEGORY_IDS } from '../constants'
import { AXES, MODES } from '../taxonomy'
import { menuTagsSchema } from '../menu-tags'
import { productTagsSchema } from '../product-tags'
import { certGoalsSchema } from '../cert/targets'
import { certEvidenceSchema, certReadinessSchema } from '../cert/readiness'
import { FORMULATION_BASES } from '@repo/food-engine'
import { ingredientLocksSchema } from '../ingredient-locks'
import { tasteProfileSchema } from '../taste-axes'
import { formulaVersionApplyFields } from './formula-versions'
import { REMOVED_CLAIMS_KEEP, removedClaimSchema } from '../unsupported-claims'
import { servingSizeSchema } from '../product-spec'

/**
 * 시뮬레이션 결과 — 모델이 JSON 으로 내고, 서버가 검증·게이트한 뒤 앱에 내려준다.
 * 이미지/영상은 여기 없음. 금액은 결과의 `currency` 하나로 통일한다
 * (ko 사용자 = 국내 시장 = KRW, 그 외 = 미국 시장 = USD. 생산은 한국이라 재료 단가는 한국 시장가에서 온다).
 *
 * 앱의 핵심 UI: 재료마다 원가가 보이고, 대체재를 토글하면 클라이언트에서 즉시 재계산.
 * 그래서 모든 대체재는 "같은 양 기준 cost" 를 이미 들고 있어야 한다 (토글 시 AI 호출 없음).
 */

/** 생성·UI 언어. ko 는 국내 시장, 나머지(en·ja·zh)는 미국 시장 규정으로 생성한다 (2026-09-14: ja·zh 추가) */
export const LANGUAGES = ['ko', 'en', 'ja', 'zh'] as const
export type Language = (typeof LANGUAGES)[number]

/** 판매 시장. 언어로 정해진다: ko → kr(식품공전), 그 외 → us(FDA/USDA) */
export const MARKETS = ['kr', 'us'] as const
export type Market = (typeof MARKETS)[number]

/** 언어별 사람이 읽는 이름 (설정 화면 등) */
export const LANGUAGE_NAMES: Record<Language, string> = {
  ko: '한국어',
  en: 'English',
  ja: '日本語',
  zh: '中文',
}

/** 언어별 Intl 날짜 표기 지역 코드 (toLocaleDateString 에 넘긴다) */
export const LANGUAGE_LOCALES: Record<Language, string> = {
  ko: 'ko-KR',
  en: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
}

export const CURRENCIES = ['KRW', 'USD'] as const
export type Currency = (typeof CURRENCIES)[number]

export function marketForLanguage(language: Language): Market {
  return language === 'ko' ? 'kr' : 'us'
}
/**
 * product.howToUse 는 결과 화면의 칩 하나(무게 · 보관 옆)에 들어가는 '먹는 법' 라벨이다 — 문장이 아니라 짧은 명사구.
 * 언어별 글자 수 상한 (2026-09-20). 저장 스키마의 max(60) 은 옛 결과 호환용으로 그대로 둔다
 */
export const HOW_TO_USE_MAX: Record<Language, number> = { ko: 14, ja: 16, zh: 12, en: 26 }
/** 옛 결과에는 howToUse 가 아예 없다 (undefined) — 없는 것은 '길지 않다' */
export function howToUseTooLong(text: string | null | undefined, language: Language): boolean {
  return [...(text ?? '').trim()].length > (HOW_TO_USE_MAX[language] ?? HOW_TO_USE_MAX.en)
}

/**
 * 화면 표시 통화 (2026-09-19 결정): 결과는 시장 통화(KRW/USD)로 계산·저장하고, 앱은 언어별 통화로 환산해 보여준다.
 * ko → KRW, ja → JPY, zh → CNY, en → USD. 환율은 GET /config 의 fx (1 USD 기준, ECB 일 1회)
 */
export const DISPLAY_CURRENCIES = ['KRW', 'USD', 'JPY', 'CNY'] as const
export type DisplayCurrency = (typeof DISPLAY_CURRENCIES)[number]
export function displayCurrencyForLanguage(language: Language): DisplayCurrency {
  if (language === 'ko') return 'KRW'
  if (language === 'ja') return 'JPY'
  if (language === 'zh') return 'CNY'
  return 'USD'
}
/** 1 USD 당 각 통화 */
export type FxRates = Record<DisplayCurrency, number>
export const FX_FALLBACK: FxRates = { USD: 1, KRW: 1350, JPY: 150, CNY: 7.2 }
/**
 * 환율 출처 (GET /config 의 fxMeta, C23). 'fallback' 은 ECB 조회가 실패해 FX_FALLBACK 고정값을 쓴 것.
 * fallback 은 고정값이 하나라도 섞였는지다 — ECB 가 일부 통화만 줘도 참. date 는 ECB 기준일(YYYY-MM-DD), 모르면 null.
 * 앱은 fallback 이면 언어 통화로 환산하지 않고 결과 통화 그대로 보이고 받는다 (고정값으로 저장한 단가가 다음 세션에 바뀌지 않게)
 */
export const FX_SOURCES = ['ecb', 'fallback'] as const
export const fxMetaSchema = z.object({
  source: z.enum(FX_SOURCES),
  date: z.string().nullable(),
  fallback: z.boolean(),
})
export type FxMeta = z.infer<typeof fxMetaSchema>
export function convertMoney(
  amount: number,
  from: DisplayCurrency,
  to: DisplayCurrency,
  rates: FxRates,
): number {
  if (from === to) return amount
  return (amount / (rates[from] || 1)) * (rates[to] || 1)
}

export function currencyForMarket(market: Market): Currency {
  return market === 'kr' ? 'KRW' : 'USD'
}

const money = z.number().nonnegative().max(1e9)
/**
 * 사람이 입력하는 개당 가격(판매가·재료 단가)의 상한 — 저장 통화 기준. 잘못 누른 자릿수(3.1e29 같은 값)를
 * 앱 입력 단계와 서버 저장(/apply) 양쪽에서 막는다 (2026-09-19)
 */
export const UNIT_PRICE_MAX: Record<Currency, number> = { KRW: 100_000_000, USD: 100_000 }
export function isSaneUnitPrice(n: number, currency: Currency): boolean {
  return Number.isFinite(n) && n >= 0 && n <= UNIT_PRICE_MAX[currency]
}

export const priceEvidenceSchema = z
  .object({
    source: z.enum(PRICE_EVIDENCE_SOURCES),
    status: z.enum(PRICE_EVIDENCE_STATUSES),
  })
  .refine((value) => value.status !== 'provided' || value.source === 'user_input')

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .default(null)
/**
 * 사용자가 넣은 재료 단가의 근거 (대표 Top10 #6, 2026-10-30). 명세 P0-04: 공급사·세금 포함 여부·확인일·유효기간을 남긴다.
 * 사용자 입력 단가(priceEvidence.source = user_input)에만 뜻이 있다 — 추정 단가에 붙어 있으면 읽는 쪽이 무시한다.
 * 유효기간이 지나면 '확인 필요'로 보인다 (cost-evidence.ts 의 priceBasisStatus). 옛 결과·옛 앱에는 없다
 */
export const PRICE_TAX_BASES = ['exclusive', 'inclusive', 'unknown'] as const
export const priceBasisSchema = z.object({
  /** 공급사 이름. 비어 있으면 공급사 견적이 아니라 사용자가 알아본 값이다 */
  supplier: z.string().trim().max(80).default(''),
  taxBasis: z.enum(PRICE_TAX_BASES).default('unknown'),
  /** 이 단가를 확인한 날 (YYYY-MM-DD) */
  checkedAt: isoDate,
  /** 이 단가가 맞는 마지막 날 (YYYY-MM-DD). 지나면 다시 확인해야 한다 */
  validUntil: isoDate,
})
export type PriceBasis = z.infer<typeof priceBasisSchema>

export const costCoverageSchema = z.object({
  complete: z.boolean(),
  missingIngredients: z.number().int().min(0).max(25),
  packagingKnown: z.boolean(),
  laborKnown: z.boolean(),
  scope: z.literal('ingredients_packaging_labor'),
})

export const simulationSubstituteSchema = z.object({
  name: z.string().min(1).max(80),
  unit: z.string().min(1).max(20),
  unitPrice: money,
  priceEvidence: priceEvidenceSchema.optional(),
  /** 원재료와 같은 qty 를 넣었을 때의 비용 */
  cost: money,
  /** 원재료 cost - 이 대체재 cost (음수면 더 비쌈) */
  savings: z.number().min(-1e9).max(1e9),
  /** 왜 이 대체재인지 한 줄 (사용자에게 보임) */
  reason: z.string().min(1).max(160),
  /** 맛·식감 영향 — 앱에서 라벨로 표시 */
  impact: z.enum(['none', 'minor', 'noticeable']),
  /** 칩에 붙는 짧은 속성 태그 (예: "wheat-free", "vegan", "cheaper"). 없으면 '' */
  tag: z.string().max(30).default(''),
})
export type SimulationSubstitute = z.infer<typeof simulationSubstituteSchema>

/**
 * 가격을 주장하는 태그인지 ("cheaper", "premium", "더 저렴", "割安", "便宜" …).
 * 대체재의 가격 차이는 실제 시세로 다시 계산한 savings 가 정답이라, 모델이 쓴 가격 태그는 숫자와 어긋날 수 있다
 * (예: tag "cheaper" 인데 +$0.04). 서버는 저장 전에 비우고, 앱은 이미 저장된 결과도 이 함수로 걸러 숫자 부호만 보여 준다.
 */
export function isPriceTag(tag: string): boolean {
  const t = tag.trim().toLowerCase()
  if (!t) return false
  return /cheap|pric|cost|expens|premium|budget|afford|saving|value|저렴|싼|싸다|비싸|비싼|절감|가격|원가|고급|가성비|安い|安価|割安|お得|高い|高価|節約|コスパ|便宜|贵|省钱|实惠|价格|高档/.test(
    t,
  )
}

/** 대략 분량 재료의 확인 상태 (C40) */
export const QTY_STATUSES = ['needs_check', 'confirmed'] as const
export type QtyStatus = (typeof QTY_STATUSES)[number]

export const simulationIngredientSchema = z.object({
  /** 토글 상태 키. 재료명 slug */
  id: z.string().min(1).max(60),
  name: z.string().min(1).max(80),
  qty: z.number().positive().max(1e6),
  unit: z.string().min(1).max(20),
  unitPrice: money,
  priceEvidence: priceEvidenceSchema.optional(),
  /** 사용자 입력 단가의 근거 (#6). 없으면 근거를 적지 않은 단가 */
  priceBasis: priceBasisSchema.optional(),
  /** qty * unitPrice (서버가 재계산해 검증) */
  cost: money,
  /** 전체 재료비 중 비중 0..1 (서버가 재계산) */
  share: z.number().min(0).max(1),
  /** 같은 차원의 투입량 대비 % (질량 또는 부피). 밀도 없는 질량/부피 혼합·모호한 단위는 null */
  ratio: z.number().min(0).max(100).nullable().default(null),
  role: z.enum(['core', 'supporting', 'optional']),
  /** 배합에서의 역할 한 줄 (예: "단맛·보습·보존") — 사용자에게 보임 */
  fn: z.string().max(80).default(''),
  /** 규제·안전 메모 (예: "카페인 0.15mg/mL 이상이면 고카페인 표시"). 없으면 '-' */
  risk: z.string().max(200).default('-'),
  substitutes: z.array(simulationSubstituteSchema).max(4),
  /**
   * 사용자가 대략 분량(숟갈·컵·줌 등)으로 적은 원문, 예 "2숟갈" (C40). 분석 모드에서 서버만 채운다.
   * qty·unit 은 그 원문을 g·ml 로 옮긴 추정치다. 없으면 정확한 분량이거나 옛 결과
   */
  sourceQty: z.string().min(1).max(40).optional(),
  /** 원문 분량을 옮긴 qty 를 사용자가 확인했는지. needs_check 동안 그 재료 원가는 추정이다 */
  qtyStatus: z.enum(QTY_STATUSES).optional(),
})
export type SimulationIngredient = z.infer<typeof simulationIngredientSchema>

/** 공정 한 단계 — 카테고리 문서 형식 */
export const simulationStepSchema = z.object({
  title: z.string().min(1).max(60),
  desc: z.string().min(1).max(600),
  /** 대표 온도 문자열, 예: "85–88℃" · "해당 없음" */
  temp: z.string().max(60),
  /** 대표 시간 문자열, 예: "15–30초" */
  time: z.string().max(60),
  equipment: z.string().max(120),
  /** 이 단계에서 실제로 측정하는 관리점만 */
  qc: z.string().max(400),
  /** 수율 손실 % */
  lossPct: z.number().min(0).max(100),
  /** 배치당 소요 (분) */
  minutes: z.number().min(0).max(100000),
})
export type SimulationStep = z.infer<typeof simulationStepSchema>

export const simulationResultSchema = z.object({
  language: z.enum(LANGUAGES),
  market: z.enum(MARKETS),
  currency: z.enum(CURRENCIES),
  /**
   * product = 가공식품(유통·표시·살균 기준), menu = 외식 메뉴(1인분 원가·조리 기준), course = 코스·테이스팅의 한 접시
   * (메뉴와 같은 1인분 기준, isMenuLikeAxis). 결과 화면 라벨이 갈린다
   */
  axis: z.enum(AXES).default('product'),
  /** create = 새로 지어낸 초안, analyze = 사용자 레시피를 그대로 옮긴 분석 */
  mode: z.enum(MODES).default('create'),
  product: z.object({
    name: z.string().min(1).max(80),
    /** 한 줄 소개 (사용자에게 보임) */
    tagline: z.string().min(1).max(140),
    categoryId: z.enum(PRODUCT_CATEGORY_IDS),
    /** 카테고리 문서의 세부유형 (예: "분말스프", "HMR") — 모델이 정함 */
    subtype: z.string().min(1).max(60),
    /** 판매 단위, 예: "12 oz jar" / "250 g 병" */
    format: z.string().min(1).max(60),
    netWeight: z.object({ amount: z.number().positive(), unit: z.string().min(1).max(10) }),
    shelfLifeDays: z.number().int().min(1).max(730),
    storage: z.enum(['ambient', 'refrigerated', 'frozen']),
    /** 먹는 법 한 토막 (예: "Microwave 3 min", "Ready to eat") — 결과 화면 핵심 칩 */
    howToUse: z.string().max(60).default(''),
    /**
     * 1회 제공량 (라벨의 '1회 제공량', 예: 15 ml · 1 tsp). AI 가 제안하고 사용자가 결과 화면에서 고친다 (2026-10-30).
     * 옛 결과·옛 앱이 보낸 저장본에는 없다 — 없으면 화면은 빈 칸으로 두고, 서버는 저장된 값을 이어 붙인다
     */
    servingSize: servingSizeSchema.optional(),
  }),
  /** 재료 수량은 이 배치 기준. units = 이 배치로 나오는 판매 단위 수 */
  batch: z.object({
    units: z.number().int().min(1).max(10000),
    yieldNote: z.string().max(160).default(''),
    /** 투입 배합비의 분모. 밀도 없는 질량/부피 혼합은 null, 옛 결과는 생략 가능 */
    ratioBasis: z.enum(FORMULATION_BASES).nullable().optional(),
  }),
  ingredients: z.array(simulationIngredientSchema).min(2).max(25),
  /** 공정 3~5단계 (카테고리 지시문이 개수를 정하면 그것 우선) — 전문 층 (Details) */
  steps: z.array(simulationStepSchema).min(3).max(5),
  /** 소비자 언어 공정 3~5줄, 동사로 시작 — 기본 뷰 */
  plainSteps: z.array(z.string().min(1).max(120)).min(3).max(5),
  costs: z.object({
    ingredientsPerUnit: money,
    packagingPerUnit: money,
    laborPerUnit: money,
    totalPerUnit: money,
    evidence: z.object({ packaging: priceEvidenceSchema, labor: priceEvidenceSchema }).optional(),
    coverage: costCoverageSchema.optional(),
  }),
  pricing: z.object({
    suggestedRetail: money,
    /** (retail - total) / retail, 0..1 */
    marginPct: z.number().min(-1).max(1),
    /** 시장 비교 가격대 — 골든셋에서 가져옴 */
    marketRange: z.object({ min: money, max: money }),
  }),
  /** 완제품 추정 pH. pH 가 관리 변수가 아니면 "해당 없음(…)" 문자열 — 절대 비우지 않는다 */
  estPh: z.string().min(1).max(120),
  /** 판매 시장 기준 표시대상 알레르겐만 */
  allergens: z.array(z.string().min(1).max(40)).max(14),
  /**
   * 재료 이름이 생성 때와 다른 채 저장됨 → allergens·규정 메모는 생성 때 값이라 다시 확인 필요 (C38).
   * 서버만 정한다(앱이 보낸 값은 버림). 없으면 표시 없음 — 옛 결과·옛 앱 호환
   */
  allergenRecheck: z.boolean().optional(),
  /** Compliance 기본 한 줄: "유통 형태 · 시장 라벨 초안 · 검증 상태" (예: Frozen ready meal · US label draft · Not lab-tested yet) */
  complianceSummary: z.string().min(1).max(120),
  /** 식품유형·유통 온도대·관할 등 규제 메모 (판매 시장 기준). Compliance Details */
  baseNote: z.string().min(1).max(2000),
  /** 검증 안내 한 줄 */
  caution: z.string().min(1).max(300),
  /**
   * 이 제품의 맛 (클릭식 맛 고르기, taste-axes.ts). AI 가 실제로 만든 배합 기준 값 — 그 카테고리 기본 축 전부와 사용자가 고른 축.
   * 맛 고르기를 켠 생성·수정에만 있다. 없으면 결과 화면에 맛 카드가 없다 (옛 결과·옛 앱 호환)
   */
  tasteProfile: tasteProfileSchema.optional(),
  /**
   * 인증 준비도 (cert/readiness.ts, 2026-11-01). 판매·인증 목표를 고른 생성에만 있고, 서버만 정한다 — 생성 게이트가 계산하고
   * 저장(/apply)·AI 수정 때 코드 검사로 다시 계산한다(앱이 보낸 값은 버림). 재료는 id 로만 가리키고 사람이 읽는 글이 없다.
   * 앱 응답에는 X-Client-Features: cert-readiness 를 보낸 앱에만 싣는다. 옛 결과·목표 없는 결과에는 없다.
   * 모양이 틀린 값은 버린다 (서버가 다시 계산한다)
   */
  certReadiness: certReadinessSchema.optional().catch(undefined),
  /** 서버 전용. 앱 응답에서 제거된다 */
  internal: z.object({
    /** 사용자가 안 준 정보를 우리가 정한 것들 — 사용자에게 절대 노출 안 함 */
    assumptions: z.array(z.string().max(200)).max(20),
    /** 생성에 참고한 골든셋 id */
    comparableIds: z.array(z.string()).max(10),
    /** 이번 생성의 다양성 시드 (같은 카테고리에서 겹치지 않게) */
    directionSeed: z.string().max(300),
    /** 생성 확정 때 판매 문구에서 자동으로 뺀 표현 (C45). 저장 때 simulations.internal 로 옮겨진다 */
    removedClaims: z.array(removedClaimSchema).max(REMOVED_CLAIMS_KEEP).optional(),
    /** 인증 준비도 근거 (규칙별 걸린 말·AI 이유, 2026-11-01). 저장 때 simulations.internal.certEvidence 로 옮겨진다 */
    certEvidence: z.array(certEvidenceSchema).max(300).optional(),
  }),
})
export type SimulationResult = z.infer<typeof simulationResultSchema>

/** 앱에 내려가는 형태 — internal 제거 */
export const simulationPublicSchema = simulationResultSchema.omit({ internal: true })
export type SimulationPublic = z.infer<typeof simulationPublicSchema>

/** 재료 이름 목록 (순서·대소문자·앞뒤 공백 무시). 알레르기 판단에 쓰는 것은 이 목록뿐이다 */
const ingredientNames = (result: Pick<SimulationPublic, 'ingredients'>) =>
  result.ingredients.map((i) => i.name.trim().toLowerCase()).sort()

/** 생성 때 재료 이름 목록(위 정리 규칙), 언어별 (C38). 서버 전용 simulations.internal 의 이 키에 둔다 */
export const GENERATION_INGREDIENTS_KEY = 'generationIngredients'
export const generationIngredientsSchema = z.partialRecord(z.enum(LANGUAGES), z.array(z.string()))
export type GenerationIngredients = z.infer<typeof generationIngredientsSchema>

/**
 * 저장할 결과의 allergenRecheck 를 정한다 (C38). 알레르기 검사는 생성 심사(gate)에서만 돌아서, 재료 이름 목록이
 * 생성 때와 다르면 다시 확인 필요로 표시하고 생성 때와 같아지면 내린다. AI 를 부르지 않고, 앱이 보낸 값은 버린다.
 *
 * 같은 언어의 이름끼리만 비교한다. 재료 id 는 언어와 무관하지만 앱에서 이름을 고치거나 대체재로 바꿔도 그대로인
 * 토글 키라 같은 재료라는 증거가 못 되고, 순서·수량은 재료가 같아도 바뀐다. 대신 생성 때 이름 목록을 언어마다 모은다:
 * 표시가 없는 저장본과 그 번역 캐시는 생성 때와 같은 재료라서 저장 때마다 그 언어 목록을 새로 얹는다
 * (번역 캐시는 저장마다 비워져, 모아 두지 않으면 다른 언어로 고친 저장을 비교할 수 없었다).
 * 그 언어의 목록이 한 번도 없었으면 비교할 수 없어 보수적으로 표시한다.
 * generationIngredients 는 저장해 둘 것이 있을 때만 돌려준다: 표시 없는 저장본이 제 언어 목록만 가지면
 * 다음 저장이 그 저장본에서 다시 만들 수 있다.
 */
export function withAllergenRecheck(
  saved: {
    result: SimulationPublic
    translations?: Partial<Record<Language, SimulationPublic>> | null
    generationIngredients?: GenerationIngredients
  },
  next: SimulationPublic,
): { result: SimulationPublic; generationIngredients?: GenerationIngredients } {
  const { allergenRecheck: _fromClient, ...rest } = next
  const generationIngredients: GenerationIngredients = { ...saved.generationIngredients }
  if (saved.result.allergenRecheck !== true) {
    for (const [language, view] of Object.entries(saved.translations ?? {}))
      if (view) generationIngredients[language as Language] = ingredientNames(view)
    generationIngredients[saved.result.language] = ingredientNames(saved.result)
  }
  const reference = generationIngredients[next.language]
  const names = ingredientNames(next)
  const same = reference?.length === names.length && reference.every((n, i) => n === names[i])
  const keep = !same || Object.keys(generationIngredients).some((l) => l !== next.language)
  return {
    result: same ? rest : { ...rest, allergenRecheck: true },
    ...(keep ? { generationIngredients } : {}),
  }
}

// ── 결과 사진 (파이프라인이 결과 저장 뒤에 생성) ─────────────────────────

/**
 * plated = 조리·플레이팅된 완성 모습 (히어로·카드) · cooking = 조리 중인 모습 (공정 한가운데)
 * packaged = 판매 포장 상태 (product 축만). 메뉴 축은 plated·cooking 2장. 전부 4:5.
 */
export const SIMULATION_IMAGE_KINDS = ['plated', 'cooking', 'packaged'] as const
export type SimulationImageKind = (typeof SIMULATION_IMAGE_KINDS)[number]

/** R2 키 형식 — GET /simulations/images/* 가 이 형식만 서빙한다 */
export const SIMULATION_IMAGE_KEY_RE =
  /^sim-images\/[0-9a-f-]{36}\/(plated|cooking|packaged)-[0-9a-f-]{36}\.(jpg|png|webp)$/

export const simulationImageSchema = z.object({
  kind: z.enum(SIMULATION_IMAGE_KINDS),
  key: z.string().regex(SIMULATION_IMAGE_KEY_RE),
})
export type SimulationImage = z.infer<typeof simulationImageSchema>

/**
 * pending = 결과는 나왔고 사진은 생성 중 (앱이 몇 초 간격으로 다시 조회)
 * done = 전부 시도함 (일부 실패면 items 가 짧다) · failed = 기획 단계부터 실패 (items 비어 있을 수 있음)
 * 시뮬레이션 status 는 첫 사진이 나오거나 사진 생성이 끝난 뒤에야 done 이 된다 — 사진 없는 결과를 앱에 띄우지 않기 위해.
 */
export const simulationImagesSchema = z.object({
  status: z.enum(['pending', 'done', 'failed']),
  items: z.array(simulationImageSchema).max(3),
  /** 플랜 때문에 만들지 않은 컷 (free 는 plated 만). 앱이 잠긴 자리로 그리고, 업그레이드 뒤 /photos/complete 로 채운다 */
  locked: z.array(z.enum(SIMULATION_IMAGE_KINDS)).max(2).default([]),
  /** AI 사진 생성이 실패한 컷 (C49). 잠긴 컷이나 이미 성공한 컷은 다시 만들기 대상이 아니다 */
  failed: z.array(z.enum(SIMULATION_IMAGE_KINDS)).max(3).optional(),
  /** 이 결과의 무료 재시도를 이미 썼나 (화면 안내용). 실제 판정은 서버가 저장한 기록으로 한다 */
  failedRetryUsed: z.boolean().optional(),
})
export type SimulationImages = z.infer<typeof simulationImagesSchema>

/** 다시 만들 실패 컷 — 실패로 기록됐지만 아직 사진이 없고 잠기지도 않은 컷 */
export function failedPhotoKinds(
  images: SimulationImages | null | undefined,
): SimulationImageKind[] {
  return [...new Set(images?.failed ?? [])].filter(
    (kind) => !images?.items?.some((item) => item.kind === kind) && !images?.locked?.includes(kind),
  )
}

/** 종류별 우선순위로 한 장 고르기 (없으면 아무거나, 그것도 없으면 null) */
export function pickSimulationImage(
  images: SimulationImages | null | undefined,
  prefer: SimulationImageKind[],
): SimulationImage | null {
  const items = images?.items ?? []
  for (const kind of prefer) {
    const hit = items.find((i) => i.kind === kind)
    if (hit) return hit
  }
  return items[0] ?? null
}

/** 게이트 결과 (어드민 Simulations 에서 봄) */
export const simulationEvaluationSchema = z.object({
  attempts: z.number().int().min(1).max(5),
  passed: z.boolean(),
  /** 마지막(또는 채택된) 시도의 검사 결과 */
  checks: z.array(
    z.object({
      name: z.string(),
      ok: z.boolean(),
      detail: z.string().max(400).optional(),
    }),
  ),
  judge: z
    .object({
      /** 1~5 */
      realism: z.number().min(1).max(5),
      consistency: z.number().min(1).max(5),
      safety: z.number().min(1).max(5),
      feedback: z.string().max(1200),
    })
    .nullable(),
  /** 파이프라인 단계별 소요(ms) */
  timingsMs: z.record(z.string(), z.number()).default({}),
})
export type SimulationEvaluation = z.infer<typeof simulationEvaluationSchema>

// ── 시뮬레이션 레코드 (서버 저장 · 앱 조회) ─────────────────────────────

/** cancelled = 사용자가 생성 중에 취소함 (C50). 이 값을 모르는 앱에는 clientSimulationStatus 가 failed 로 바꿔 보낸다 */
export const SIMULATION_STATUS = ['queued', 'running', 'done', 'failed', 'cancelled'] as const
export type SimulationStatus = (typeof SIMULATION_STATUS)[number]

/** 결과 없이 끝난 생성(실패·취소). 앱은 이 상태에서 폴링을 멈추고 결과 대신 안내를 그린다 */
export function endedWithoutResult(status: SimulationStatus): boolean {
  return status === 'failed' || status === 'cancelled'
}

/**
 * 실패한 생성을 사용자가 같은 입력으로 다시 돌리는 횟수 상한 (POST /simulations/:id/retry, 1.0.1).
 * 결과 화면과 제품 카드가 같이 쓴다. 다 쓰면 앱은 '새로 만들기'로 안내한다
 */
export const SIMULATION_RETRY_LIMIT = 2
/** 409: 같은 생성의 재시도 상한(SIMULATION_RETRY_LIMIT)을 다 썼다 */
export const RETRY_LIMIT_CODE = 'RETRY_LIMIT'
/** 409: 실패 상태가 아니라서 다시 돌릴 수 없다 (이미 다시 도는 중 · 완성됨) */
export const NOT_RETRYABLE_CODE = 'NOT_RETRYABLE'

/**
 * 앱이 처리할 줄 아는 기능을 서버에 알리는 헤더 (1.0.1 부터 모든 요청에 실음). 값은 쉼표로 이은 CLIENT_FEATURE_* 목록.
 * 옛 서버는 이 헤더를 무시하고, 1.0.0 앱은 보내지 않는다 — 서버는 헤더가 없으면 지금 동작 그대로 둔다
 */
export const CLIENT_FEATURES_HEADER = 'X-Client-Features'
/** '다시 만들기'(POST /simulations/:id/retry)를 그릴 줄 안다 → 이 앱이 만든 생성이 실패하면 실패 알림(sim_failed)을 보낸다 */
export const CLIENT_FEATURE_SIM_RETRY = 'sim-retry'
/**
 * 새 분류(국제·서양 메인, 차·허브, 식물성·비건, 새 세부)와 메뉴 꼬리표를 그릴 줄 안다 (2026-10-22).
 * 없으면(1.0.3·1.0.4) 서버가 응답의 majorId·subId 를 legacySafeTaxonomy 로 낮춘다. 1.0.5 도 이 값을 싣지만 1.0.6 분류는 몰라서
 * 서버는 X-App-Version 으로 한 번 더 가른다 (clientTaxonomyKeys)
 */
export const CLIENT_FEATURE_TAXONOMY_V2 = 'taxonomy-v2'
/**
 * 코스·테이스팅 축(axis course, 2026-11-01)을 그릴 줄 안다. 없는 앱(1.0.3~1.0.8)은 축 두 개만 알아 모르는 축의 분류 표를 찾다
 * 화면이 깨진다. 서버가 그 앱에 보내는 코스 접시는 분류 없는 메뉴로 낮춘다 (clientTaxonomyKeys·withKnownTaxonomy)
 */
export const CLIENT_FEATURE_COURSE_AXIS = 'course-axis'
/**
 * 인증 준비도 카드(result.certReadiness, 2026-11-01)를 그릴 줄 안다. 이 값을 보낸 앱에만 서버가 결과에 준비도를 싣는다
 * (옛 앱은 모르는 칸을 버리지만, 공유·캐시로 새어 나가지 않게 처음부터 보내지 않는다)
 */
export const CLIENT_FEATURE_CERT_READINESS = 'cert-readiness'

/** 헤더 값에 이 기능이 들어 있나 (공백·대소문자 무시) */
export function hasClientFeature(header: string | null | undefined, feature: string): boolean {
  if (!header) return false
  return header
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .includes(feature)
}

/**
 * 상태 cancelled(사용자가 생성을 취소함, C50)를 그릴 줄 안다. 이 값이 없는 앱(1.0.3~1.0.7)은 모르는 상태를 진행 중으로 그리거나
 * 응답을 버리므로, 서버가 그 앱에 보내는 상태는 clientSimulationStatus 로 failed 로 낮춘다
 */
export const CLIENT_FEATURE_GENERATION_CANCEL = 'generation-cancel'

/** 앱에 보낼 상태. cancelled 는 그것을 아는 앱(CLIENT_FEATURE_GENERATION_CANCEL)에만 그대로, 나머지에는 failed */
export function clientSimulationStatus(
  status: SimulationStatus,
  clientFeatures: string | null | undefined,
): SimulationStatus {
  return status === 'cancelled' &&
    !hasClientFeature(clientFeatures, CLIENT_FEATURE_GENERATION_CANCEL)
    ? 'failed'
    : status
}

/**
 * 생성 취소 (POST /simulations/:id/cancel, C50). queued = AI 를 아직 부르지 않은 단계라 생성 사용권을 돌려준다.
 * running = AI 를 이미 불렀으므로 돌려주지 않는다
 */
export const GENERATION_CANCEL_STAGES = ['queued', 'running'] as const
export type GenerationCancelStage = (typeof GENERATION_CANCEL_STAGES)[number]
/** 409: 취소할 수 없다 (이미 끝났거나 실패함, 결과가 확정됨) */
export const NOT_CANCELLABLE_CODE = 'NOT_CANCELLABLE'

/**
 * 파이프라인이 지금 어디까지 왔는지 (로딩 화면의 실제 진행률·문구용). 서버가 internal.progress 에 쓰고 API 가 progress 로 내려준다.
 * prepare → [generate → prices → gate] × 최대 maxAttempts → save → photos(사진 한 장씩)
 */
export const SIMULATION_STAGES = [
  'prepare',
  /** core 초안(제품명·재료·가격)을 스트리밍으로 받는 중 — progress.core 가 채워진다 */
  'core',
  /** 상세(fn·risk·대체재·공정·규정)·시세·심판을 동시에 돌리는 중 */
  'detail',
  // 아래 셋은 옛 파이프라인 단계 — 남아 있는 행의 progress 를 읽기 위해 유지
  'generate',
  'prices',
  'gate',
  'save',
  'photos',
] as const
export type SimulationStage = (typeof SIMULATION_STAGES)[number]

/**
 * 스트리밍 중 먼저 도착하는 결과 조각. 앱은 이걸로 결과 화면을 스켈레톤 상태로 연다
 * (제품명·재료 이름이 오면 열고, 가격·상세·사진은 result 가 저장될 때까지 스켈레톤).
 */
export const simulationCoreSchema = z.object({
  name: z.string().max(80).default(''),
  tagline: z.string().max(140).default(''),
  format: z.string().max(60).default(''),
  netWeight: z
    .object({ amount: z.number(), unit: z.string().max(10) })
    .nullable()
    .default(null),
  storage: z.enum(['ambient', 'refrigerated', 'frozen']).nullable().default(null),
  howToUse: z.string().max(60).default(''),
  ingredients: z
    .array(
      z.object({
        name: z.string().max(80),
        qty: z.number().nullable().default(null),
        unit: z.string().max(20).nullable().default(null),
      }),
    )
    .max(25)
    .default([]),
  plainSteps: z.array(z.string().max(120)).max(8).default([]),
  /** 모델이 초안을 다 보냈는지 (재료 목록이 완결됐는지) */
  complete: z.boolean().default(false),
})
export type SimulationCore = z.infer<typeof simulationCoreSchema>

export const simulationProgressSchema = z.object({
  stage: z.enum(SIMULATION_STAGES),
  /** 몇 번째 시도인지 (1부터). generate/prices/gate 에서 의미 있음 */
  attempt: z.number().int().min(1),
  maxAttempts: z.number().int().min(1),
  /** prices 단계: 이번 초안의 재료 수 (가격을 붙이는 대상) */
  ingredientCount: z.number().int().nullable(),
  /** photos 단계: 지금까지 나온 장수 / 찍을 장수 */
  photosDone: z.number().int(),
  photosTotal: z.number().int(),
  /** core 단계에서 스트리밍으로 채워지는 결과 조각. 이후 단계에서도 유지 */
  core: simulationCoreSchema.nullable().default(null),
  /** 1차안은 저장됐지만 게이트에 걸려 다시 만드는 중 — 앱은 '다듬는 중' 배지를 띄우고 결과가 바뀌면 교체 */
  refining: z.boolean().default(false),
  updatedAt: z.string(),
})
export type SimulationProgress = z.infer<typeof simulationProgressSchema>

/** 게스트 시뮬레이션 보관 시간 — 24시간 (2026-09-14 결정: 가입 유도 카운트다운). 지나면 조회 불가 */
export const SIMULATION_GUEST_TTL_MS = 24 * 60 * 60 * 1000

/** 로그인 없는 기기 식별 헤더. 앱이 설치 시 만든 UUID 를 항상 보낸다 */
export const DEVICE_ID_HEADER = 'X-Device-Id'

/**
 * 만들기 입력 한도 — 서버 검증(아래 스키마)과 앱 입력창이 같은 값을 쓴다. 넘기면 POST 가 400 이라
 * 앱이 먼저 막는다 (2026-09-29 — 7장·4001자에서 생성이 매번 실패하고 빠져나갈 길이 없었다)
 */
export const SIMULATION_INPUT_LIMITS = {
  /** 한 제품에 올릴 수 있는 레시피 사진 (모든 메시지 합계) */
  images: 6,
  /** 메시지 1개의 글자 수 */
  textLength: 4000,
  /** 메시지 1개에 붙이는 사진·영상 수 */
  mediaPerMessage: 20,
  /** 메시지 수 */
  messages: 30,
} as const

/** R2 에 올린 레시피 사진 키 (POST /simulations/uploads 가 돌려줌) */
const imageKeys = z
  .array(z.string().regex(/^sim-uploads\/[A-Za-z0-9_-]+\/[0-9a-f-]{36}\.(jpg|png|webp)$/))
  .max(SIMULATION_INPUT_LIMITS.images)
  .default([])

const inputMessages = z
  .array(
    z.object({
      text: z.string().max(SIMULATION_INPUT_LIMITS.textLength),
      mediaCount: z.number().int().min(0).max(SIMULATION_INPUT_LIMITS.mediaPerMessage).default(0),
    }),
  )
  .max(SIMULATION_INPUT_LIMITS.messages)
  .default([])

/**
 * 아이디어로 시작할 때 묻는 네 질문의 허용 값 (C39). 'unknown' = 모름. 답이 그대로 프롬프트 조각이 되므로
 * 자유 글 없이 이 값만 받는다. 순서는 화면 칩 순서다
 */
export const START_GOAL_OPTIONS = {
  /** 사용 목적 */
  purpose: ['launch', 'menu', 'gift', 'hobby', 'unknown'],
  /** 목표 수량 (첫 생산·판매 규모) */
  targetQty: ['trial', 'small', 'medium', 'large', 'unknown'],
  /** 예산 (원가를 얼마나 쓸지) */
  budget: ['low', 'mid', 'premium', 'unknown'],
  /** 판매 대상 */
  audience: ['general', 'family', 'health', 'young', 'business', 'unknown'],
} as const
export type StartGoalQuestion = keyof typeof START_GOAL_OPTIONS
export const START_GOAL_QUESTIONS: readonly StartGoalQuestion[] = [
  'purpose',
  'targetQty',
  'budget',
  'audience',
]

/** 네 질문 모두 건너뛸 수 있다 (칸 없음 = 건너뜀) */
export const startGoalsSchema = z.object({
  purpose: z.enum(START_GOAL_OPTIONS.purpose).optional(),
  targetQty: z.enum(START_GOAL_OPTIONS.targetQty).optional(),
  budget: z.enum(START_GOAL_OPTIONS.budget).optional(),
  audience: z.enum(START_GOAL_OPTIONS.audience).optional(),
})
export type StartGoals = z.infer<typeof startGoalsSchema>

/**
 * POST /simulations 입력 — 모드 + 축 + 대/중카테고리 + A2 설명(텍스트 + 업로드한 사진 키) + 기기 언어.
 * categoryId 는 클라이언트가 taxonomy 로 계산해 보낸다 (프롬프트·골든셋·맛 칩의 키).
 */
export const createSimulationSchema = z.object({
  /** One intentional submission. Optional for older apps; never an authorization credential. */
  requestKey: z
    .string()
    .min(16)
    .max(80)
    .regex(/^[A-Za-z0-9_-]+$/)
    .optional(),
  categoryId: z.enum(PRODUCT_CATEGORY_IDS),
  mode: z.enum(MODES).default('create'),
  /** 옛 앱은 product·menu 만 보낸다. course(코스·테이스팅)는 1.0.9~ 앱이 서버 features.courseTasting 을 본 뒤에만 보낸다 */
  axis: z.enum(AXES).default('product'),
  /** 2단 카테고리 선택 (taxonomy). 옛 클라이언트는 비움. course 축은 majorId = 코스 역할, subId = 스타일·기법 */
  majorId: z.string().max(40).nullable().default(null),
  subId: z.string().max(80).nullable().default(null),
  language: z.enum(LANGUAGES).default('en'),
  /** A2 맛/스타일 칩 id (TASTE_CHIPS). 비어 있으면 방향 시드가 무작위로 정한다 (analyze 모드에선 무시) */
  tastes: z.array(z.string().max(40)).max(8).default([]),
  messages: inputMessages,
  images: imageKeys,
  /** 판매 시장. Pro(quota.marketSelect) 만 반영되고 나머지는 언어 기준 (marketForLanguage) */
  market: z.enum(MARKETS).optional(),
  /** 패키지 형태 (GET /simulations/package-refs 의 form). 첫 사진을 이 용기 레퍼런스에 음식만 바꿔 넣어 만든다. 없으면 첫 형태 */
  packageForm: z.string().max(40).nullable().default(null),
  /**
   * '이 사례로 시작하기'로 만든 제품의 원본 사례 id (sample_cases.id, 2026-10-22). 입력이 통째로 simulations.input 에
   * 저장되므로 생성 코드는 고치지 않는다. 서버는 진짜 사례인지 확인하지 않는다 (집계 숫자에만 쓴다, 설계 6.3). 옛 앱은 보내지 않는다.
   * 사례 시작 스위치가 꺼져 있으면 이 칸이 있는 새 생성은 409 CASE_START_DISABLED 다
   */
  sourceCaseId: z.uuid().optional(),
  /**
   * 메뉴 축 다축 꼬리표 (선택, menu-tags.ts). 서버가 normalizeMenuTags 로 정리해 input 에 저장하고 프롬프트 힌트로만 쓴다.
   * product 축이면 서버가 버린다. 옛 앱은 보내지 않는다. 이 타입은 저장된 input 의 타입이기도 해서
   * (꼬리표 전 행에는 칸이 없다) default 대신 optional 이다
   */
  menuTags: menuTagsSchema.optional(),
  /**
   * 아이디어 시작 질문의 답 (선택, C39). 하나라도 고른 create 모드 요청만 싣는다. 입력이 통째로 simulations.input 에
   * 저장되고 프롬프트가 허용 값만 조각으로 붙인다. 옛 앱은 보내지 않고, 옛 서버는 모르는 칸이라 버린다
   */
  startGoals: startGoalsSchema.optional(),
  /**
   * 클릭식 맛 고르기의 목표 맛 (선택). 맛 축 카드를 그린 새 앱만 싣는다 (아무 축도 안 골랐으면 빈 values).
   * 서버가 축 표와 플랜으로 걸러 저장하고, 스위치가 꺼져 있으면 버린다. 옛 서버는 모르는 칸이라 버린다
   */
  tasteProfile: tasteProfileSchema.optional(),
  /**
   * 식품 축 속성 꼬리표 (선택, product-tags.ts, 2026-11-01). 비건·저당·보관 온도처럼 분류와 따로 고르는 조건.
   * 서버가 normalizeProductTags 로 정리해 input 에 저장하고 create 모드 프롬프트에만 쓴다. 다른 축이면 버린다.
   * 옛 앱은 보내지 않고, 옛 서버는 모르는 칸이라 버린다. 저장된 input 의 타입이기도 해서 optional 이다
   */
  productTags: productTagsSchema.optional(),
  /**
   * 판매·인증 목표 (선택, cert/targets.ts, 2026-11-01). 할랄·해썹(1단계) 중 고른 것과 할랄 판매국.
   * 서버가 normalizeCertGoals 로 정리해(식품 축만, 해썹은 한국 시장만, 관리자 스위치 settings.certReadiness) 저장하고,
   * 그 기준을 생성 프롬프트·심사·준비도에 쓴다. 비면 칸을 두지 않는다 — 목표 없는 요청의 input·프롬프트는 지금과 같다.
   * 옛 앱은 보내지 않고, 옛 서버는 모르는 칸이라 버린다. 저장된 input 의 타입이기도 해서 optional 이다.
   * 모르는 목표·판매국 id(새 앱이 보낸 다음 단계 값)는 요청 전체를 400 으로 막지 않고 이 칸만 버린다
   */
  certGoals: certGoalsSchema.optional().catch(undefined),
})
export type CreateSimulationInput = z.infer<typeof createSimulationSchema>

/** PATCH /simulations/:id/menu-tags — 결과 화면의 꼬리표 수정. 결과·사진은 다시 만들지 않는다 */
export const updateMenuTagsSchema = z.object({ menuTags: menuTagsSchema })
export type UpdateMenuTagsInput = z.infer<typeof updateMenuTagsSchema>

/** GET /simulations/package-refs — 카테고리의 용기 형태 목록 */
export const packageRefsQuerySchema = z.object({
  axis: z.enum(AXES).default('product'),
  majorId: z.string().max(40),
  subId: z.string().max(80).optional(),
  language: z.enum(LANGUAGES).default('en'),
})
export interface PackageFormOption {
  form: string
  label: string
  /** 썸네일: GET /simulations/package-refs/thumb/<cutId> */
  cutId: string
}

/** POST /simulations/:id/photos/package — 용기 바꿔서 패키지 사진 다시 만들기 */
export const changePackageSchema = z.object({ form: z.string().max(40) })
export type ChangePackageInput = z.infer<typeof changePackageSchema>

/** POST /simulations/check-recipe — analyze 모드에서 Simulate 전에 레시피 자료가 충분한지 LLM 판정 */
export const checkRecipeSchema = z.object({
  axis: z.enum(AXES).default('product'),
  majorId: z.string().max(40).nullable().default(null),
  subId: z.string().max(80).nullable().default(null),
  language: z.enum(LANGUAGES).default('en'),
  messages: inputMessages,
  images: imageKeys,
})
export type CheckRecipeInput = z.infer<typeof checkRecipeSchema>

export const recipeCheckResultSchema = z.object({
  sufficient: z.boolean(),
  /** 부족할 때 사용자에게 보여줄 한 문단 (기기 언어). 충분하면 '' */
  feedback: z.string().max(600),
  /** 빠진 항목 (재료 분량, 만드는 법 …) */
  missing: z.array(z.string().max(80)).max(8),
})
export type RecipeCheckResult = z.infer<typeof recipeCheckResultSchema>

/** POST /simulations/:id/edit — AI 와 대화로 결과 수정. draft = 이전 턴까지 수정된 결과 (없으면 저장본에서 시작) */
export const editSimulationSchema = z.object({
  messages: z
    .array(z.object({ role: z.enum(['user', 'assistant']), content: z.string().min(1).max(2000) }))
    .min(1)
    .max(30),
  draft: simulationPublicSchema.nullable().default(null),
  ingredientLocks: ingredientLocksSchema.optional(),
  /**
   * 결과 화면 '맛 다시 맞추기'의 목표 맛 (선택). 서버가 플랜으로 거른 뒤 수정 지시로 바꿔 AI 수정 1턴으로 돌린다.
   * 옛 앱은 보내지 않는다
   */
  tasteProfile: tasteProfileSchema.optional(),
})
export type EditSimulationInput = z.infer<typeof editSimulationSchema>

export interface EditSimulationReply {
  /** Fingerprint of the owned source result used to prepare this edit; never refresh an existing draft with it. */
  baseRevision?: string
  /** 사용자에게 보여줄 짧은 답 (바뀐 점) */
  reply: string
  changes: string[]
  result: SimulationPublic
}

/** POST /simulations/:id/apply — 수정본 저장. overwrite = 덮어쓰기, duplicate = 복사본으로 (플랜 한도 적용) */
export const applyEditSchema = z.object({
  result: simulationPublicSchema,
  mode: z.enum(['overwrite', 'duplicate']),
  /** Omitted by installed legacy clients. Empty/malformed values are not a legacy request. */
  baseRevision: editBaseRevisionSchema.optional(),
  /** Omitted by legacy clients: keep the saved locks. [] clears them (C37). */
  ingredientLocks: ingredientLocksSchema.optional(),
  /** 배합 버전 이력 (C42): 함께 본 지금 버전·저장 이유·되돌릴 버전. 모두 선택 칸 */
  ...formulaVersionApplyFields,
})
export type ApplyEditInput = z.infer<typeof applyEditSchema>

/** PATCH /simulations/:id — 제품명 */
export const renameSimulationSchema = z.object({ productName: z.string().trim().min(1).max(80) })
